import { StoreService } from '@hb/services/store/store.service';
import { AccordionStep, FieldSubject, IncomeOption } from '@hb/domain';
import { ApplicantDetailsService } from '@hb/services/applicant-details/applicant-details.service';
import { ApplicantsBuilder } from '@hb/builder/applicants-builder/applicantsBuilder';
import { ApplicantBuilder } from '@hb/builder';
import { AbstractStoreStepInject } from '@hb/services/store/abstract-store-step-inject.service';
import { AccordionStateService } from '@hb/services/accordion-state/accordion-state.service';

import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable, skip } from 'rxjs';

import { Money } from '@ap/interface-bspc-core';
import { ApplicantDetailsResponse, Applicants, StartersDispensation } from '@ap/ap-interface-apix-hypotheken-berekening';

@Injectable({
    providedIn: 'root'
})
export class ApplicantsStoreService extends AbstractStoreStepInject implements OnDestroy {
    protected readonly storeService = inject(StoreService);
    protected readonly accordionStateService = inject(AccordionStateService);

    protected readonly applicantDetailsService = inject(ApplicantDetailsService);

    private readonly isLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly applicantsSubject: FieldSubject<Applicants>;

    private readonly mainApplicantIncomeSubject: BehaviorSubject<Money>;
    private readonly coApplicantIncomeSubject: BehaviorSubject<Money>;
    private readonly mainApplicantDateOfBirthSubject: BehaviorSubject<Date>;
    private readonly coApplicantDateOfBirthSubject: BehaviorSubject<Date>;
    private readonly startersDispensationSubject: BehaviorSubject<StartersDispensation>;

    private readonly mainApplicantDetailsSubject: BehaviorSubject<ApplicantDetailsResponse> = new BehaviorSubject<ApplicantDetailsResponse>(
        null
    );

    private readonly coApplicantDetailsSubject: BehaviorSubject<ApplicantDetailsResponse> = new BehaviorSubject<ApplicantDetailsResponse>(
        null
    );

    constructor() {
        super();
        this.summary = IncomeOption.SUMMARY;
        this.activeOptionSubject = new BehaviorSubject(
            this.accordionStateService.getStepIsFinished(AccordionStep.INCOME) ? IncomeOption.SUMMARY : IncomeOption.INCOME_INPUT
        );

        const applicantsState = this.getApplicantsState();
        this.applicantsSubject = new FieldSubject<Applicants>(applicantsState);

        this.mainApplicantIncomeSubject = new BehaviorSubject<Money>(applicantsState?.main_applicant?.income);
        this.coApplicantIncomeSubject = new BehaviorSubject<Money>(applicantsState?.co_applicant?.income);

        this.mainApplicantDateOfBirthSubject = new BehaviorSubject<Date>(
            applicantsState?.main_applicant?.date_of_birth ? new Date(applicantsState.main_applicant.date_of_birth) : null
        );
        this.coApplicantDateOfBirthSubject = new BehaviorSubject<Date>(
            applicantsState?.co_applicant?.date_of_birth ? new Date(applicantsState.co_applicant.date_of_birth) : null
        );

        this.startersDispensationSubject = new BehaviorSubject<StartersDispensation>(applicantsState?.starters_dispensation);

        this.setUp();
    }

    setUp(): void {
        this.storeService
            .getHasCoApplicant()
            .pipe(distinctUntilChanged(), skip(1))
            .subscribe((hasCoApplicant) => this.coApplicantUpdate(hasCoApplicant));
        this.getApplicants().subscribe((applicants: Applicants) => this.storeService.setApplicants(applicants));
        this.getMainApplicantIncome()
            .pipe(skip(1))
            .subscribe(() => this.update());
        this.getCoApplicantIncome()
            .pipe(skip(1))
            .subscribe(() => this.update());

        this.getMainApplicantDateOfBirth()
            .pipe(distinctUntilChanged(), skip(1))
            .subscribe(() => {
                this.setMainApplicantDetails();
                this.update();
            });
        this.getCoApplicantDateOfBirth()
            .pipe(distinctUntilChanged(), skip(1))
            .subscribe(() => {
                this.update();
                this.setCoApplicantDetails();
            });
        this.getStartersDispensation()
            .pipe(skip(1))
            .subscribe(() => this.update());
    }

    getIsLoading(): Observable<boolean> {
        return this.isLoadingSubject.asObservable();
    }

    getStartersDispensation(): Observable<StartersDispensation> {
        return this.startersDispensationSubject.asObservable();
    }

    getApplicantsState(): Applicants {
        return this.storeService.getState()?.applicants;
    }

    getApplicants(): Observable<Applicants> {
        return this.applicantsSubject.asObservable();
    }

    getMainApplicantIncome(): Observable<Money> {
        return this.mainApplicantIncomeSubject.asObservable();
    }

    getCoApplicantIncome(): Observable<Money> {
        return this.coApplicantIncomeSubject.asObservable();
    }

    getMainApplicantDateOfBirth(): Observable<Date> {
        return this.mainApplicantDateOfBirthSubject.asObservable();
    }

    getCoApplicantDateOfBirth(): Observable<Date> {
        return this.coApplicantDateOfBirthSubject.asObservable();
    }

    setMainApplicantIncome(income: Money): void {
        this.mainApplicantIncomeSubject.next(income);
    }

    setCoApplicantIncome(income: Money): void {
        this.coApplicantIncomeSubject.next(income);
    }

    setMainApplicantDateOfBirth(date: Date): void {
        this.mainApplicantDateOfBirthSubject.next(date);
    }

    setCoApplicantDateOfBirth(date: Date): void {
        this.coApplicantDateOfBirthSubject.next(date);
    }

    getMainApplicantDetails(): Observable<ApplicantDetailsResponse> {
        return this.mainApplicantDetailsSubject.asObservable();
    }

    getCoApplicantDetails(): Observable<ApplicantDetailsResponse> {
        return this.coApplicantDetailsSubject.asObservable();
    }

    ngOnDestroy(): void {
        this.activeOptionSubject.unsubscribe();
        this.applicantsSubject.unsubscribe();
        this.mainApplicantIncomeSubject.unsubscribe();
        this.coApplicantIncomeSubject.unsubscribe();
        this.mainApplicantDateOfBirthSubject.unsubscribe();
        this.coApplicantDateOfBirthSubject.unsubscribe();
        this.startersDispensationSubject.unsubscribe();
    }

    isActiveOption(question: IncomeOption): boolean {
        switch (question) {
            case IncomeOption.INCOME_INPUT:
            case IncomeOption.DATE_OF_BIRTH:
                return true;
            case IncomeOption.AOW10YEARS_INFO:
                return this.showAowInfo();
            case IncomeOption.STARTERS_DISPENSATION:
            case IncomeOption.STARTERS_DISPENSATION_INFO:
                return this.showStartersDispensation();
            case IncomeOption.SUMMARY:
            default:
                return false;
        }
    }

    showStartersDispensation(): boolean {
        return this.mainApplicantDetailsSubject.value?.starter_dispensation || this.coApplicantDetailsSubject.value?.starter_dispensation;
    }

    private update(): void {
        const startersDispensationOption = this.startersDispensationSubject.value;
        const mainApplicant = new ApplicantBuilder()
            .withIncome(this.mainApplicantIncomeSubject.value)
            .withDateOfBirth(this.mainApplicantDateOfBirthSubject.value)
            .build();

        if (this.coApplicantIncomeSubject.value != null) {
            const coApplicant = new ApplicantBuilder()
                .withIncome(this.coApplicantIncomeSubject.value)
                .withDateOfBirth(this.coApplicantDateOfBirthSubject.value)
                .build();
            this.applicantsSubject.next(
                new ApplicantsBuilder()
                    .withMainApplicant(mainApplicant)
                    .withCoApplicant(coApplicant)
                    .withStartersDispensation(startersDispensationOption)
                    .build()
            );
        } else {
            this.applicantsSubject.next(
                new ApplicantsBuilder().withMainApplicant(mainApplicant).withStartersDispensation(startersDispensationOption).build()
            );
        }
    }

    setDisplayFullAccordion() {
        this.accordionStateService.setDisplayFullAccordion();
    }

    private setMainApplicantDetails() {
        this.isLoadingSubject.next(true);
        this.applicantDetailsService.getApplicantDetails(this.mainApplicantDateOfBirthSubject.value).subscribe({
            next: (applicantDetails: ApplicantDetailsResponse) => {
                if (
                    this.mainApplicantDetailsSubject.value &&
                    applicantDetails?.starter_dispensation !== this.mainApplicantDetailsSubject.value?.starter_dispensation
                ) {
                    this.setStartersDispensation(null);
                }
                this.mainApplicantDetailsSubject.next(applicantDetails);
            },
            error: () => {
                this.isLoadingSubject.next(false);
            },
            complete: () => {
                this.isLoadingSubject.next(false);
            }
        });
    }

    private setCoApplicantDetails() {
        if (this.coApplicantDateOfBirthSubject.value != null) {
            this.isLoadingSubject.next(true);
            this.applicantDetailsService.getApplicantDetails(this.coApplicantDateOfBirthSubject?.value).subscribe({
                next: (applicantDetails: ApplicantDetailsResponse) => {
                    if (
                        this.coApplicantDetailsSubject.value &&
                        applicantDetails?.starter_dispensation !== this.coApplicantDetailsSubject.value?.starter_dispensation
                    ) {
                        this.setStartersDispensation(null);
                    }
                    this.coApplicantDetailsSubject.next(applicantDetails);
                },
                error: () => {
                    this.isLoadingSubject.next(false);
                },
                complete: () => {
                    this.isLoadingSubject.next(false);
                }
            });
        } else {
            this.coApplicantDetailsSubject.next(null);
        }
    }

    setStartersDispensation(startersDispensation: StartersDispensation) {
        this.startersDispensationSubject.next(startersDispensation);
    }

    private showAowInfo() {
        return this.mainApplicantDetailsSubject.value?.aow10yr || this.coApplicantDetailsSubject.value?.aow10yr;
    }

    setStartersDispensationInfoFinished() {
        this.nextOption();
    }

    setAowInfoFinished() {
        this.nextOption();
    }

    private coApplicantUpdate(hasCoApplicant: boolean): void {
        this.setMainApplicantDateOfBirth(null);
        if (!hasCoApplicant) {
            this.setCoApplicantIncome(null);
        }
        this.setCoApplicantDateOfBirth(null);

        this.setStartersDispensation(null);

        this.accordionStateService.setStepNotFinished(AccordionStep.INCOME);
        this.activeOptionSubject.next(IncomeOption.INCOME_INPUT);
    }
}
